import { arrayMove } from '@dnd-kit/sortable';
import { useCallback, useMemo } from 'react';

import { NavLink } from '~/eds/src/types/NavLink';
import { useUserPreferences } from '~/redux/api/rolodex/useUserPreferences';

/**
 * Every sub-nav entry, keyed by the pathname a pin is stored against. Section
 * roots are left out: a root is a heading for the entries under it, so pinning
 * one would put a second copy of that heading above the nav rather than the
 * destination someone wanted. Two sections can offer the same destination, and
 * the first one wins — both rows toggle the same pin either way.
 */
function collectSubNavLinks(items: NavLink[]) {
  const subNavLinks = new Map<string, NavLink>();

  for (const item of items) {
    for (const child of item.children ?? []) {
      if (!subNavLinks.has(child.pathname)) {
        subNavLinks.set(child.pathname, child);
      }
    }
  }

  return subNavLinks;
}

/**
 * Which nav entries are pinned and the order they show in, persisted as a user
 * preference. The cache is patched as it happens, so pinning and dragging move
 * under the pointer, and the write that follows is debounced — dragging a row
 * through three positions is one save, not three.
 */
export function useSidebarPinnedLinks(items: NavLink[]) {
  const {
    pinnedNavItems: pinnedPathnames,
    setPinnedNavItems: applyPinnedPathnames,
  } = useUserPreferences();

  const subNavLinks = useMemo(() => collectSubNavLinks(items), [items]);

  // A pin whose entry is gone — a permission change, or a saved search someone
  // deleted — drops out of the list rather than rendering a dead row. It stays
  // in the stored order, so it comes back if the entry does.
  const pinnedLinks = useMemo(
    () =>
      pinnedPathnames.flatMap((pathname) => {
        const link = subNavLinks.get(pathname);
        return link ? [link] : [];
      }),
    [subNavLinks, pinnedPathnames],
  );

  const togglePin = useCallback(
    (pathname: string) => {
      applyPinnedPathnames(
        pinnedPathnames.includes(pathname)
          ? pinnedPathnames.filter((pinned) => pinned !== pathname)
          : [...pinnedPathnames, pathname],
      );
    },
    [applyPinnedPathnames, pinnedPathnames],
  );

  const reorderPinned = useCallback(
    (fromPathname: string, toPathname: string) => {
      const from = pinnedPathnames.indexOf(fromPathname);
      const to = pinnedPathnames.indexOf(toPathname);
      if (from < 0 || to < 0 || from === to) return;
      applyPinnedPathnames(arrayMove(pinnedPathnames, from, to));
    },
    [applyPinnedPathnames, pinnedPathnames],
  );

  const isPinned = useCallback(
    (pathname: string) => pinnedPathnames.includes(pathname),
    [pinnedPathnames],
  );

  return { isPinned, pinnedLinks, reorderPinned, togglePin };
}
